import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import NavBar from "../../../Common/NavBar";
import { TiposVehiculoURL } from "../../../API/apiurls";

const buildPreviewRows = (positions) => {
  if (!Array.isArray(positions) || !positions.length) return [];
  const normalized = positions.map((item) => ({
    ...item,
    code: String(item.codigo || "").trim().toUpperCase(),
  }));

  const hasThreeLiteras = normalized.some((item) => item.code === "LITERA_3");
  const hasLiteraAtras = normalized.some((item) => item.code === "LITERA_ATRAS");
  const hasCentro = normalized.some((item) => item.code === "CENTRO");

  if (hasThreeLiteras) {
    return [
      normalized.filter((item) => ["CONDUCTOR", "COPILOTO"].includes(item.code)).map((item) => item.nombre),
      normalized.filter((item) => item.code.startsWith("LITERA_")).map((item) => item.nombre),
    ].filter((row) => row.length);
  }

  if (hasCentro) {
    return [normalized.map((item) => item.nombre)];
  }

  if (hasLiteraAtras) {
    return [
      normalized.filter((item) => ["CONDUCTOR", "COPILOTO"].includes(item.code)).map((item) => item.nombre),
      normalized.filter((item) => item.code === "LITERA_ATRAS").map((item) => item.nombre),
    ].filter((row) => row.length);
  }

  return [normalized.map((item) => item.nombre)];
};

export function PosicionesAdmin() {
  const navigation = useNavigate();
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadTipos = async () => {
      try {
        setLoading(true);
        const res = await axios.get(TiposVehiculoURL, { params: { activo: true } });
        setTipos(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Error cargando tipos de vehículo:", error);
        setTipos([]);
      } finally {
        setLoading(false);
      }
    };
    loadTipos();
  }, []);

  const totalPosiciones = useMemo(
    () => tipos.reduce((acc, tipo) => acc + Number(tipo?.posiciones?.length || 0), 0),
    [tipos]
  );

  return (
    <div>
      <NavBar />
      <div className="camionesMenu-contenedor admin-crud-shell">
        <Button style={{ width: "100%", marginBottom: 12 }} onClick={() => navigation("/panel-administrador")}>Atrás</Button>

        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, marginBottom: 16 }}>
          <div style={{ color: "white" }}>
            <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 4 }}>Posiciones por tipo de vehículo</div>
            <div style={{ opacity: 0.85 }}>
              Esta pantalla ya no muestra posiciones sueltas. Ahora cada posición pertenece a un tipo de vehículo definido en la API.
            </div>
          </div>
          <Button variant="outline-light" onClick={() => navigation("/vehiculos-admin")}>Administrar vehículos</Button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 18 }}>
          <div style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: 14, background: "rgba(255,255,255,0.03)", color: "white" }}>
            <div style={{ fontSize: 12, opacity: 0.75 }}>Tipos activos</div>
            <strong style={{ fontSize: 26 }}>{tipos.length}</strong>
          </div>
          <div style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: 14, background: "rgba(255,255,255,0.03)", color: "white" }}>
            <div style={{ fontSize: 12, opacity: 0.75 }}>Posiciones definidas</div>
            <strong style={{ fontSize: 26 }}>{totalPosiciones}</strong>
          </div>
        </div>

        {loading ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", color: "white" }}><Spinner size="sm" /> Cargando tipos de vehículo...</div>
        ) : !tipos.length ? (
          <div style={{ color: "white", opacity: 0.85 }}>No hay tipos de vehículo configurados.</div>
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            {tipos.map((tipo) => {
              const previewRows = buildPreviewRows(tipo.posiciones || []);
              return (
                <div
                  key={tipo.id}
                  style={{
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 16,
                    padding: 16,
                    background: "rgba(255,255,255,0.03)",
                    color: "white",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{tipo.codigo}</div>
                      <div style={{ fontSize: 22, fontWeight: 800 }}>{tipo.nombre}</div>
                      <div style={{ opacity: 0.85, marginTop: 4 }}>{tipo.descripcion || "Sin descripción"}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>Cinturones esperados</div>
                      <div style={{ fontSize: 24, fontWeight: 800 }}>{tipo.cantidadCinturones || 0}</div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "minmax(260px, 1.2fr) minmax(260px, 1fr)", gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 10 }}>Preview estructural</div>
                      <div
                        style={{
                          borderRadius: 14,
                          border: "1px solid rgba(255,255,255,0.08)",
                          background: "rgba(5,14,24,0.75)",
                          padding: 14,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        {previewRows.map((row, rowIndex) => (
                          <div
                            key={`${tipo.id}-row-${rowIndex}`}
                            style={{
                              display: "grid",
                              gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
                              gap: 10,
                            }}
                          >
                            {row.map((posicion) => (
                              <span
                                key={`${tipo.id}-${posicion}`}
                                style={{
                                  padding: "12px 10px",
                                  borderRadius: 12,
                                  background: rowIndex === 0 ? "rgba(47, 134, 255, 0.14)" : "rgba(255, 176, 32, 0.14)",
                                  border: rowIndex === 0 ? "1px solid rgba(47, 134, 255, 0.24)" : "1px solid rgba(255, 176, 32, 0.24)",
                                  fontSize: 12,
                                  textAlign: "center",
                                  minHeight: 48,
                                  display: "grid",
                                  placeItems: "center",
                                }}
                              >
                                {posicion}
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 10 }}>Posiciones asignadas al tipo</div>
                      <div style={{ display: "grid", gap: 10 }}>
                        {(tipo.posiciones || []).map((posicion) => (
                          <div
                            key={posicion.id}
                            style={{
                              border: "1px solid rgba(255,255,255,0.10)",
                              borderRadius: 12,
                              padding: 12,
                              background: "rgba(255,255,255,0.02)",
                              display: "grid",
                              gridTemplateColumns: "120px 1fr auto",
                              gap: 12,
                              alignItems: "center",
                            }}
                          >
                            <strong>{posicion.codigo}</strong>
                            <span>{posicion.nombre}</span>
                            <span style={{ opacity: 0.8 }}>Orden {posicion.orden}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
